'use client';

import { useState } from 'react';
import { ThumbsUp, ThumbsDown, X, Send } from 'lucide-react';
import { useGuidedTour } from './GuidedTourProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface GuidedTourFeedbackProps {
  stepId?: string;
  onSubmit?: (feedback: { rating: number; helpful: boolean; comment?: string }) => void;
}

export function GuidedTourFeedback({ stepId, onSubmit }: GuidedTourFeedbackProps) {
  const { isOpen, currentStep, closeTour, moduleKey, engine } = useGuidedTour();
  const [rating, setRating] = useState(0);
  const [helpful, setHelpful] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) {
    return null;
  }

  const targetStepId = stepId ?? currentStep?.id;

  const handleSubmit = () => {
    if (helpful === null) {
      return;
    }

    const feedback = {
      rating,
      helpful,
      comment: comment.trim() || undefined,
    };

    onSubmit?.(feedback);
    setSubmitted(true);

    // Track feedback analytics
    fetch('/api/v1/training/tour-analytics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        event_type: 'feedback',
        tour_key: moduleKey,
        flow_id: engine.selectedFlowId,
        step_id: targetStepId,
        feedback_rating: rating,
        feedback_comment: comment.trim() || undefined,
      }),
    }).catch(() => {});
  };

  if (submitted) {
    return (
      <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,24rem)]">
        <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
          <CardContent className="py-8 text-center">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Thanks for your feedback!</p>
            <Button className="mt-4" size="sm" variant="outline" onClick={closeTour}>
              Close
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="pointer-events-auto fixed bottom-4 right-4 z-[70] w-[min(92vw,24rem)]">
      <Card className="border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur-md dark:border-slate-700/80 dark:bg-slate-800/95">
        <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
          <CardTitle className="text-base dark:text-slate-100">How was this step?</CardTitle>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={closeTour}>
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          <div className="flex gap-2">
            <Button
              variant={helpful === true ? 'default' : 'outline'}
              size="sm"
              className={cn('flex-1 gap-2', helpful === true && 'bg-emerald-600 hover:bg-emerald-700')}
              onClick={() => {
                setHelpful(true);
                setRating(Math.max(rating, 4));
              }}
            >
              <ThumbsUp className="h-4 w-4" />
              Helpful
            </Button>
            <Button
              variant={helpful === false ? 'default' : 'outline'}
              size="sm"
              className={cn('flex-1 gap-2', helpful === false && 'bg-rose-600 hover:bg-rose-700')}
              onClick={() => {
                setHelpful(false);
                setRating(Math.min(rating, 2));
              }}
            >
              <ThumbsDown className="h-4 w-4" />
              Not helpful
            </Button>
          </div>

          <Textarea
            placeholder="Any comments? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[80px] text-sm dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
          />

          <Button
            size="sm"
            className="w-full gap-2"
            disabled={helpful === null}
            onClick={handleSubmit}
          >
            <Send className="h-4 w-4" />
            Submit feedback{targetStepId ? ` for step ${targetStepId}` : ''}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
